import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress, IconButton, InputAdornment, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { NotificationType } from '../../config';
import { AddressType, BpType } from '../../config/company';
import useConfig from '../../context/ConfigContext';
import useUsers from '../../context/UsersContext';
import navOnlineService from '../../service/navOnline.service';
import addNotification from '../../utils/addNotification';

const CompanyTaxpayerLookup = ({ disabled = false, simple = false }) => {
  const { values, setValues, isSubmitting, submitCount } = useFormikContext();
  const { config } = useConfig();
  const { user } = useUsers();
  const [loading, setLoading] = useState(false);
  const [navAvailable, setNavAvailable] = useState(false);
  const currentValues = useRef(values);
  const currentSubmitCount = useRef(submitCount);
  const controller = useRef(null);
  currentValues.current = values;
  currentSubmitCount.current = submitCount;

  useEffect(() => () => controller.current?.abort(), []);

  const billingAddress = simple
    ? values.address?.[0]
    : values.address?.find(
        (address) => address.adressType === AddressType.BILLING && address.default && address.active !== false
      ) || values.address?.find((address) => address.adressType === AddressType.BILLING && address.active !== false);
  const countryCode = billingAddress?.countryCode || (simple ? config.defCompanyCountry : 'HU');
  const available = values.bpType === BpType.COMPANY && (!countryCode || countryCode === 'HU');

  useEffect(() => {
    setNavAvailable(false);
    if (!available) return;
    const availabilityController = new AbortController();
    navOnlineService
      .getTaxpayerQueryAvailability(availabilityController.signal)
      .then((result) => {
        if (!availabilityController.signal.aborted) setNavAvailable(result?.available === true);
      })
      .catch(() => {
        if (!availabilityController.signal.aborted) setNavAvailable(false);
      });
    return () => availabilityController.abort();
  }, [available]);

  const loadTaxpayer = async () => {
    if (controller.current || disabled || isSubmitting || !available || !navAvailable) return;
    const taxNumber = values.hunTaxNumber?.trim();
    if (!taxNumber) return;
    const requestController = new AbortController();
    controller.current = requestController;
    setLoading(true);
    try {
      const result = await navOnlineService.queryTaxpayer(taxNumber, 'HU', requestController.signal);
      if (requestController.signal.aborted) return;
      if (currentValues.current !== values || currentSubmitCount.current !== submitCount) {
        addNotification({
          type: NotificationType.WARNING,
          content: 'Az űrlap adatai közben megváltoztak. Indítsd újra az adatbetöltést.',
        });
        return;
      }
      if (!result.valid || !result.verified || result.provider !== 'NAV') {
        addNotification({
          type: NotificationType.WARNING,
          content: result.message || 'A NAV nem igazolta az adószám érvényességét.',
        });
        return;
      }
      const taxpayer = result.taxpayer;
      const address = taxpayer?.addresses?.find((item) => item.type === 'HQ') || taxpayer?.addresses?.[0];
      if (!taxpayer?.name || !address?.postalCode || !address.city || (!address.streetName && !address.lotNumber)) {
        addNotification({
          type: NotificationType.WARNING,
          content: 'A NAV nem adott vissza teljes név- és címadatokat.',
        });
        return;
      }
      const addressData = {
        countryCode: address.countryCode || 'HU',
        province: address.region || '',
        postCode: address.postalCode,
        city: address.city,
        streetAddress: [
          address.streetName,
          address.publicPlaceCategory,
          address.number,
          address.building,
          address.staircase,
          address.floor,
          address.door,
          address.lotNumber,
        ]
          .filter(Boolean)
          .join(' '),
      };
      await setValues((previous) => {
        const addresses = [...(previous.address || [])];
        let index = simple
          ? 0
          : addresses.findIndex(
              (item) => item.adressType === AddressType.BILLING && item.default && item.active !== false
            );
        if (index < 0)
          index = addresses.findIndex((item) => item.adressType === AddressType.BILLING && item.active !== false);
        if (index < 0) index = addresses.length;
        const existing = addresses[index];
        addresses[index] = {
          ...(existing ||
            (simple
              ? {}
              : {
                  id: uuidv4(),
                  isNew: true,
                  name: config.defCompanyBillingAddr || 'Számlázási cím',
                  adressType: AddressType.BILLING,
                  active: true,
                  default: true,
                  createdBy: user.userId,
                })),
          ...addressData,
        };
        return {
          ...previous,
          name: taxpayer.name,
          ...(!simple && taxpayer.shortName ? { shortName: taxpayer.shortName } : {}),
          address: addresses,
        };
      });
      addNotification({
        type: NotificationType.SUCCESS,
        content: 'A név és a számlázási cím betöltése sikerült.',
      });
    } catch (error) {
      const alreadyNotified =
        error?.config && (error?.response?.data?.error?.message || (!error.response && error.message));
      if (!requestController.signal.aborted && !alreadyNotified) {
        addNotification({
          type: NotificationType.ERROR,
          content: error?.response?.data?.error?.message || 'A NAV-adatbetöltés sikertelen.',
        });
      }
    } finally {
      controller.current = null;
      if (!requestController.signal.aborted) setLoading(false);
    }
  };

  if (!available || !navAvailable) return null;

  return (
    <InputAdornment position="end">
      <Tooltip title="Név és cím betöltése adószám alapján">
        <span>
          <IconButton
            type="button"
            size="small"
            edge="end"
            aria-label="Név és cím keresése adószám alapján"
            disabled={disabled || isSubmitting || loading || !values.hunTaxNumber?.trim()}
            onClick={loadTaxpayer}
          >
            {loading ? <CircularProgress size={18} /> : <SearchIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </InputAdornment>
  );
};

export default CompanyTaxpayerLookup;
